import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'Does Dino Nuggets work with Michigan-based businesses specifically?',
    answer: 'Absolutely. We work with small businesses across Michigan — from Detroit metro service companies to Grand Rapids contractors and northern Michigan seasonal businesses. Our AI is trained on your specific business, your Michigan service area, and your local customer needs.',
  },
  {
    question: 'How quickly can my Michigan business get set up?',
    answer: 'Most Michigan businesses are live within 24–48 hours. We handle all setup, AI training, and testing remotely — no in-person visits needed. You\'ll be answering calls and booking jobs automatically in under two days.',
  },
  {
    question: 'What types of Michigan businesses benefit most from AI automation?',
    answer: 'Home service businesses see the biggest immediate return — contractors, HVAC companies, plumbers, landscapers, cleaning services, and more. But AI automation in Michigan works for any small business that relies on phone calls, appointment bookings, or customer follow-up.',
  },
  {
    question: 'Is AI automation affordable for a small Michigan business?',
    answer: 'Yes. Our plans are priced specifically for small businesses — a fraction of the cost of hiring even a part-time receptionist. Most Michigan businesses recover the monthly cost from just one or two additional jobs captured by the AI.',
  },
  {
    question: 'Do I need any technical knowledge to use this?',
    answer: 'Not at all. We handle the entire setup and training. You don\'t need to know anything about AI or technology. If you can use a smartphone, you can run Dino Nuggets.',
  },
];

const michiganCities = [
  'Detroit', 'Grand Rapids', 'Ann Arbor', 'Lansing', 'Flint',
  'Sterling Heights', 'Warren', 'Kalamazoo', 'Troy', 'Traverse City',
];

const services = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering',
    desc: 'Your AI receptionist answers every call immediately — day, night, weekends. Michigan customers get a professional response the moment they call, not a voicemail.',
    link: '/ai-phone-answering-service',
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Website Chatbot',
    desc: 'Capture leads from your website around the clock. Your AI chatbot engages Michigan homeowners and business owners, qualifies them, and books appointments automatically.',
    link: '/services/ai-chatbot',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Scheduling',
    desc: 'Michigan customers book service appointments directly — your AI checks your calendar in real time and confirms instantly. Zero back-and-forth, zero phone tag.',
    link: '/ai-appointment-scheduling',
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Marketing Automation',
    desc: 'Automated follow-up, review requests, and re-engagement campaigns keep your Michigan customers coming back and new leads moving through your pipeline.',
    link: null,
  },
];

const whyUs = [
  {
    icon: 'ri-store-2-line',
    title: 'Built for Small Businesses',
    desc: 'Not enterprise software crammed into a small business package. Every feature was designed from the ground up for independent businesses like yours.',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Easy to Implement',
    desc: 'We handle 100% of the setup, training, and testing. You\'re live in 24–48 hours with zero disruption to your current operations.',
  },
  {
    icon: 'ri-user-smile-line',
    title: 'No Tech Experience Needed',
    desc: 'If you can use a smartphone, you can use Dino Nuggets. No IT staff, no technical background, no learning curve.',
  },
  {
    icon: 'ri-coin-line',
    title: 'Affordable Pricing',
    desc: 'Plans built for Michigan small business budgets. Most businesses recoup the monthly cost from a single additional job the AI captures.',
  },
];

const useCases = [
  {
    icon: 'ri-tools-line',
    title: 'Contractors in Michigan',
    desc: 'From Detroit remodelers to Grand Rapids builders — Michigan contractors use AI automation to capture every lead, book every estimate, and stop losing business to voicemail.',
    link: '/ai-for-contractors',
    img: '/readdy-assets/mi-usecase-contractor-001.jpg',
  },
  {
    icon: 'ri-plant-line',
    title: 'Landscapers in Michigan',
    desc: 'Spring and fall seasons are brutal for call volume. Michigan landscapers let AI automation handle the rush — booking lawn care, cleanups, and landscape consultations automatically.',
    link: '/ai-for-landscapers',
    img: '/readdy-assets/mi-usecase-landscaper-001.jpg',
  },
  {
    icon: 'ri-home-8-line',
    title: 'Home Service Businesses',
    desc: 'Cleaners, HVAC companies, plumbers, electricians — any Michigan home service business with customers calling to book jobs is a perfect fit for AI automation.',
    link: '/industries/ai-for-home-services',
    img: '/readdy-assets/mi-usecase-homeservice-001.jpg',
  },
];

export default function AiAutomationMichigan() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const geoTags: Array<[string, string]> = [
      ['geo.region', 'US-MI'],
      ['geo.placename', 'Michigan'],
      ['geo.position', '44.3148;-85.6024'],
      ['ICBM', '44.3148, -85.6024'],
    ];

    const created: Element[] = [];

    geoTags.forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
        created.push(tag);
      }
      tag.setAttribute('content', content);
    });

    return () => {
      created.forEach(el => el.remove());
    };
  }, []);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI Automation Michigan | AI Tools for Michigan Small Businesses | Dino Nuggets"
        description="AI automation in Michigan built for small businesses. Dino Nuggets helps Michigan contractors, landscapers, and service businesses answer calls 24/7, book more jobs, and automate customer communication."
        canonical="/ai-automation-michigan"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Michigan Small Businesses',
              description: 'AI phone answering, chatbot, automated scheduling, and marketing automation for small businesses across Michigan.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: {
                '@type': 'State',
                name: 'Michigan',
                sameAs: 'https://www.wikidata.org/wiki/Q1166',
              },
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section className="relative pt-28 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/michigan-hero-001.jpg"
              alt="AI automation for Michigan small businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white/96 via-white/82 to-white/35"></div>

          <div className="relative max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide uppercase">
                Michigan Local AI
              </span>
              <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                AI Automation for<br />
                <span className="text-primary">Small Businesses in Michigan</span>
              </h1>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed">
                Helping Michigan businesses automate calls, scheduling, and customer communication with AI — so you can focus on the work, not the phone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
                >
                  Book a Free Consultation
                </button>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="border-2 border-gray-300 hover:border-primary hover:text-primary text-gray-700 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-lg"
                >
                  See How It Works
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-6 mt-8">
                {['Serving all of Michigan', 'Live in 24–48 hours', 'No tech skills needed'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-primary text-lg"></i>
                    </div>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CITY TAGS ────────────────────────────────────────────── */}
        <section className="bg-gray-50 py-6 px-6 lg:px-8 border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 justify-center">
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Serving Michigan businesses in:</span>
              {michiganCities.map(city => (
                <span key={city} className="bg-white border border-gray-200 text-gray-700 text-sm px-3 py-1 rounded-full whitespace-nowrap">
                  <i className="ri-map-pin-2-fill text-primary mr-1 text-xs"></i>
                  {city}
                </span>
              ))}
              <span className="text-sm text-gray-500 whitespace-nowrap">+ across the state</span>
            </div>
          </div>
        </section>

        {/* ── LOCAL FOCUS ──────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Michigan Focus</span>
                <h2 className="font-display text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Why Michigan Small Businesses Need AI Automation Now
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Michigan's small business market is competitive. From the Detroit metro area to Grand Rapids, Lansing, and beyond — local service businesses are competing for the same customers, and the business that responds first almost always wins the job.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5">
                  The challenge? Michigan's busiest small business owners — contractors, cleaners, landscapers, HVAC technicians — can't answer every call while they're working. That means missed calls, lost leads, and revenue walking straight to a competitor who picked up.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  AI automation in Michigan solves this completely. Your AI handles every call, responds to every website visitor, and books appointments automatically — 24/7 — while you focus entirely on delivering great service to your existing customers.
                </p>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-7 py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Book a Free Consultation
                </button>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {[
                  { stat: '70%', label: 'of Michigan callers hang up before leaving a voicemail' },
                  { stat: '5 min', label: 'response window before Michigan leads move on to a competitor' },
                  { stat: '24/7', label: 'coverage — including Michigan winters when demand spikes at odd hours' },
                  { stat: '2 days', label: 'average time from signup to your AI going live for Michigan customers' },
                ].map(item => (
                  <div key={item.stat} className="bg-gray-50 rounded-xl p-6 text-center border border-gray-100">
                    <div className="text-3xl font-extrabold text-primary mb-2">{item.stat}</div>
                    <p className="text-gray-600 text-xs leading-relaxed">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICES ─────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">AI Tools</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                AI Tools for Michigan Small Businesses
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                A complete set of AI tools designed specifically for small business operations. Everything you need, nothing you don't.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map(item => (
                <div key={item.title} className="bg-white p-7 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all group flex flex-col">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-5 group-hover:bg-primary/20 transition-colors">
                    <i className={`${item.icon} text-2xl text-primary`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed flex-1">{item.desc}</p>
                  {item.link && (
                    <Link
                      to={item.link}
                      className="inline-flex items-center gap-1.5 text-primary text-sm font-medium mt-5 hover:underline cursor-pointer"
                    >
                      Learn more <i className="ri-arrow-right-line"></i>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY US ───────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-1 gap-5">
                  {whyUs.map(item => (
                    <div key={item.title} className="flex gap-5 p-6 bg-gray-50 rounded-xl hover:bg-primary/5 transition-colors group">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <i className={`${item.icon} text-2xl text-primary`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-base mb-1.5">{item.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Why Choose Us</span>
                <h2 className="font-display text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Why Michigan Businesses Choose Dino Nuggets for AI Automation
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  There are a lot of AI tools out there — most of them built for enterprise companies with big IT teams and bigger budgets. We built Dino Nuggets specifically for local Michigan small businesses that need results fast, without the complexity.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Our AI tools for Michigan small businesses are ready to deploy in days, priced for real business budgets, and backed by a team that actually understands what it takes to run a local service business in a competitive Michigan market.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setIsCalendarOpen(true)}
                    className="bg-primary hover:bg-primary/90 text-white px-7 py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    Book a Free Consultation
                  </button>
                  <Link
                    to="/checkout"
                    className="border-2 border-gray-300 hover:border-primary hover:text-primary text-gray-700 px-7 py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-center"
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── USE CASES ────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-secondary to-secondary/90">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-white/10 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Michigan Use Cases</span>
              <h2 className="font-display text-4xl font-bold text-white mb-4">
                Michigan Businesses Already Winning with AI Automation
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
                AI automation in Michigan is working right now for businesses just like yours. Here's how.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {useCases.map(item => (
                <div key={item.title} className="bg-white rounded-xl overflow-hidden group hover:shadow-lg transition-all">
                  <div className="h-52 overflow-hidden">
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-xl text-primary`}></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{item.desc}</p>
                    <Link
                      to={item.link}
                      className="inline-flex items-center gap-1.5 text-primary text-sm font-medium hover:underline cursor-pointer"
                    >
                      See how it works <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ─────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">
                What Michigan Business Owners Are Saying
              </h2>
              <p className="text-gray-600">Real results from real businesses using AI automation in Michigan.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: 'I run a contracting company out of Grand Rapids and was losing leads every single day. Since setting up Dino Nuggets, my AI answers every call and books the estimate. Game-changer.',
                  name: 'Ryan M.',
                  role: 'General Contractor, Grand Rapids, MI',
                },
                {
                  quote: 'My landscaping crew is busy all spring and I can\'t answer my phone. AI automation in Michigan has been the best investment I\'ve made. My schedule fills itself now.',
                  name: 'Tara B.',
                  role: 'Landscaping Business, Lansing, MI',
                },
                {
                  quote: 'We\'re a small HVAC company in the Detroit suburbs. The AI handles calls 24/7 including emergencies. Setup took less than two days and it\'s been running perfectly ever since.',
                  name: 'Kevin D.',
                  role: 'HVAC Company Owner, Sterling Heights, MI',
                },
              ].map(t => (
                <div key={t.name} className="bg-white p-7 rounded-xl border border-gray-200">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">&ldquo;{t.quote}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── INTERNAL LINKS ───────────────────────────────────────── */}
        <section className="py-14 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-primary/5 rounded-2xl p-10">
              <div className="text-center mb-8">
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">
                  Explore More AI Tools &amp; Industries
                </h2>
                <p className="text-gray-600 text-sm max-w-lg mx-auto">
                  Browse our full lineup of AI automation services and industry-specific pages.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Full virtual front desk for Michigan businesses.' },
                  { to: '/ai-phone-answering-service', icon: 'ri-phone-line', title: 'AI Phone Answering', desc: 'Never miss a Michigan customer call again.' },
                  { to: '/ai-appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Appointment Scheduling', desc: 'Automate your Michigan business bookings.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'AI Website Chatbot', desc: 'Capture Michigan web leads 24/7.' },
                  { to: '/ai-for-contractors', icon: 'ri-tools-line', title: 'AI for Contractors', desc: 'Automation built for Michigan contractors.' },
                  { to: '/industries/ai-for-home-services', icon: 'ri-home-8-line', title: 'AI for Home Services', desc: 'Every Michigan home service trade covered.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-0.5 text-sm">{item.title}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">FAQ</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Questions About AI Automation in Michigan
              </h2>
              <p className="text-gray-600 leading-relaxed">Common questions from Michigan business owners before getting started.</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 pt-1">
                      <p className="text-gray-700 leading-relaxed text-sm">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ────────────────────────────────────────────── */}
        <section className="py-24 px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/michigan-cta-bg-001.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-secondary/90"></div>
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block bg-primary/20 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wide">
              Michigan Small Business AI
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
              Grow Your Michigan Business with AI
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Book a free 15-minute consultation and see exactly how AI automation in Michigan works for your specific business. No commitment, no tech headaches.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-10 py-5 rounded-lg font-bold transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-lg"
            >
              Get Started Today
            </button>
            <p className="text-gray-500 text-sm mt-5">Free 15-min call · Serving all of Michigan · Live in 24–48 hours</p>
          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
